<template>
  <div :class="iconClasses" :aria-label="props.ariaLabel" :aria-hidden="!props.ariaLabel">
    <slot />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import type { IconSize } from '@/types/ui'

interface Props {
  size?: IconSize
  variant?: 'primary' | 'secondary' | 'success' | 'warning' | 'danger' | 'purple' | 'ghost' | 'blue'
  ariaLabel?: string
}

const props = withDefaults(defineProps<Props>(), {
  size: 'regular'
  // variant is optional - no default, so icons inherit color when not provided
})

/** Normalize legacy size tokens (medium → regular, xs/xsmall → small). */
function resolveIconSize(size: IconSize | undefined): 'small' | 'regular' | 'large' | 'extra-large' {
  if (!size || size === 'medium' || size === 'regular') return 'regular'
  if (size === 'xs' || size === 'xsmall' || size === 'sm') return 'small'
  return size
}

// Map size to classes
const sizeClasses = computed(() => {
  const sizeMap: Record<string, string> = {
    small: 'w-4 h-4',
    regular: 'w-5 h-5',
    large: 'w-6 h-6',
    'extra-large': 'w-8 h-8'
  }
  const resolved = resolveIconSize(props.size)
  return sizeMap[resolved] || sizeMap.regular
})

// Map variant to classes - only apply when variant is explicitly provided
const variantClasses = computed(() => {
  // If variant is not provided, return empty string so icon inherits color from parent
  if (!props.variant) {
    return ''
  }
  
  const variantMap: Record<string, string> = {
    primary: 'text-icon-primary hover:text-icon-primary-hover',
    secondary: 'text-icon-secondary hover:text-icon-secondary-hover',
    success: 'text-icon-success hover:text-icon-success-hover',
    warning: 'text-icon-warning hover:text-icon-warning-hover',
    danger: 'text-icon-danger hover:text-icon-danger-hover',
    purple: 'text-icon-purple hover:text-icon-purple-hover',
    ghost: 'text-icon-ghost hover:text-icon-ghost-hover',
    blue: 'text-badge-blue'
  }
  return variantMap[props.variant] || ''
})

// Combine all classes
const iconClasses = computed(() => {
  return [
    'inline-flex items-center justify-center ',
    sizeClasses.value,
    variantClasses.value,
    '[&>svg]:w-full [&>svg]:h-full [&>svg]:stroke-current'
  ].join(' ')
})
</script>
